import { useMutation } from '@apollo/client';
import { RemoveParticipantFromChatConversationDocument } from '@website/types/graphqlOperations';
import { useRouter } from 'next/router';
import { Button, Spinner } from 'react-bootstrap';
import { FaBan } from 'react-icons/fa';
import ConfirmDialog from '@website/components/utilities/ConfirmDialog';
import { useState } from 'react';

type Props = {
  conversationId: string;
  userID: string;
};

export default function LeaveGroupConversation({
  conversationId,
  userID,
}: Props) {
  const [isConfirmDialogueVisible, setIsConfirmDialogueVisible] =
    useState(false);

  const [leaveConversation, { loading }] = useMutation(
    RemoveParticipantFromChatConversationDocument,
    {
      update(cache, { data }) {
        // remove the conversation from the cache, so it doesn't show up in the
        // inbox
        const conversationCacheID = cache.identify({
          __typename: 'ChatConversation',
          id: data.removeParticipantFromChatConversation.id,
        });
        cache.evict({ id: conversationCacheID });
      },
    },
  );

  const { asPath, push } = useRouter();

  const onLeaveConversation = async () => {
    try {
      await leaveConversation({
        variables: {
          input: {
            conversationID: conversationId,
            participantID: userID,
          },
        },
      });

      // redirect back to inbox
      push((asPath as string).replace(conversationId, ''));
    } catch (e) {
      if (e.networkError) {
        alert(
          'Internet connection lost - please retry when you are connected.',
        );
      } else {
        alert(e);
      }
    }
  };

  if (!conversationId.startsWith('group_')) {
    return null;
  }

  return (
    <>
      <ConfirmDialog
        setVisible={(visible) => setIsConfirmDialogueVisible(visible)}
        title="Leaving group chat"
        body={`Are you sure you want to leave the group chat?`}
        visible={isConfirmDialogueVisible}
        onCancelFn={() => setIsConfirmDialogueVisible(false)}
        onConfirmFn={() => onLeaveConversation()}
      />

      <Button
        size="sm"
        variant="link"
        className="text-dark"
        onClick={() => setIsConfirmDialogueVisible(true)}
        title="Leave group chat"
      >
        {loading ? (
          <Spinner
            animation="border"
            size="sm"
            as="span"
            style={{ position: 'relative', top: -3 }}
          />
        ) : (
          <FaBan size={20} />
        )}
      </Button>
    </>
  );
}
